import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, catchError, combineLatest, EMPTY, firstValueFrom, map, switchMap } from 'rxjs';
import { SpanishAcknowledgmentDialogComponent } from 'projects/borrower-app/src/app/spanish-acknowledgment/spanish-acknowledgment-dialog.component';
import { AnonymousPortalSettingsService } from 'projects/borrower-app/src/app/services/anonymous-portal-settings.service';
import { Constants } from 'projects/borrower-app/src/app/services/constants';
import { DataService } from 'projects/borrower-app/src/app/services/data.service';
import { legalDocumentsPageHref } from 'projects/borrower-app/src/app/services/legal-documents';
import { NavigationService } from 'projects/borrower-app/src/app/services/navigation.service';
import {
  decideGate,
  missingOnBehalf,
  PENDING_ACK_KEY,
  PendingAcknowledgment,
  SpanishAcknowledgmentContent,
  usablePending,
} from 'projects/borrower-app/src/app/services/spanish-acknowledgment';
import { signInIdentity } from 'projects/borrower-app/src/app/services/ui-language';
import { LoadedApplication, UiLanguageService } from 'projects/borrower-app/src/app/services/ui-language.service';

const url = (applicationId: number) => `api/BorrowerPortal/applications/${applicationId}/spanish-acknowledgment`;

/** GET api/BorrowerPortal/applications/{id}/spanish-acknowledgment for the signed-in caller. */
export interface SpanishAcknowledgmentStatus {
  required: boolean;
  /** Null when the lender has the acknowledgment off, or the caller is not a borrower on the application. */
  content: SpanishAcknowledgmentContent | null;
  /** Borrowers on the application with a record of the current version. */
  acknowledgedBorrowerIds: number[];
}

type PostReason = 'accepted' | 'pending' | 'on-behalf';

/**
 * LLS-886 §5 — the Spanish language acknowledgment. While the app is in Spanish and an application is loaded
 * for a signed-in borrower, the server says whether this borrower still needs to acknowledge the current
 * wording on that application; if so a blocking dialog asks (declining switches back to English). A signed-out
 * switch to Spanish asks first (confirmSwitchToSpanish) and the acceptance is saved once the application is
 * known. Co-borrowers the primary applies for in the wizard are recorded as acknowledged by the primary, the
 * way e-consent is recorded for them.
 */
@Injectable({ providedIn: 'root' })
export class SpanishAcknowledgmentService {
  private _app: LoadedApplication | null = null;
  private _status: SpanishAcknowledgmentStatus | null = null;
  /** Co-borrowers the primary applies for, and the application the wizard reported them for. */
  private _appliedFor: { applicationId: number; borrowerIds: number[] } | null = null;
  private _dialogOpen = false;
  private _posting = false;
  /** An open signed-out dialog; a second ES click waits for it instead of opening another. */
  private _signedOutPrompt: Promise<boolean> | null = null;
  /** Automatic saves already tried (application|version|borrowers) — a failure is not retried in a loop. */
  private readonly _attempted = new Set<string>();
  private readonly _reload = new BehaviorSubject<void>(undefined);

  constructor(
    private readonly _dataService: DataService,
    private readonly _transloco: TranslocoService,
    private readonly _uiLanguage: UiLanguageService,
    private readonly _modal: NgbModal,
    private readonly _anonymousPortalSettings: AnonymousPortalSettingsService,
    private readonly _navigation: NavigationService,
    private readonly _window: Window,
  ) {
    combineLatest([this._transloco.langChanges$, this._uiLanguage.application$, this._reload]).pipe(
      switchMap(([lang, app]) => {
        if (app?.applicationId !== this._app?.applicationId || app?.session !== this._app?.session) {
          this._app = app;
          this._status = null;
        }
        const current = this._app;
        if (lang !== 'es' || !current) {
          return EMPTY;
        }
        return this._dataService.get(url(current.applicationId)).pipe(
          map((status: SpanishAcknowledgmentStatus) => ({ app: current, status })),
          catchError(() => EMPTY), // fail open: the display language is not blocked by an unreachable status
        );
      }),
    ).subscribe(({ app, status }) => {
      if (app !== this._app) {
        return; // another application or sign-in loaded meanwhile
      }
      this._status = status;
      this.evaluate();
    });
  }

  /**
   * Wizard: contact ids of the co-borrowers the signed-in primary applies for on <applicationId> (see
   * appliedForCoBorrowerIds). Only used while that application is the loaded one, so a borrower who moves to another
   * loan never sends the previous loan's co-borrowers (the Api would reject them, and the dialog would come back).
   */
  setAppliedForBorrowers(applicationId: number, ids: number[]): void {
    const previous = this._appliedFor;
    if (previous?.applicationId === applicationId && ids.length === previous.borrowerIds.length
      && ids.every(id => previous.borrowerIds.includes(id))) {
      return;
    }
    this._appliedFor = { applicationId, borrowerIds: [...ids] };
    this.evaluate();
  }

  private appliedForCurrentApplication(): number[] {
    return this._appliedFor && this._appliedFor.applicationId === this._app?.applicationId ? this._appliedFor.borrowerIds : [];
  }

  /**
   * Language switcher, before applying Spanish. Signed out with the acknowledgment on: ask now and keep an
   * acceptance for after sign-in; false = declined, stay in the current language. Otherwise true (a signed-in
   * borrower is asked by the gate once the language changes).
   */
  confirmSwitchToSpanish(): Promise<boolean> {
    this._signedOutPrompt ??= this.askSignedOut().finally(() => this._signedOutPrompt = null);
    return this._signedOutPrompt;
  }

  private async askSignedOut(): Promise<boolean> {
    const content = await firstValueFrom(this._anonymousPortalSettings.spanishAcknowledgment$);
    if (!content || this.isSignedIn()) {
      return true;
    }
    const accepted = await this.openDialog(content);
    if (accepted) {
      this.writePending({ version: content.version, acceptedAt: Date.now(), companyGuid: this._navigation.companyGuid ?? '' });
    }
    return accepted;
  }

  /** Whether the lender's acknowledgment is on (it replaces the informational Spanish toggle notice). */
  async isEnabled(): Promise<boolean> {
    return !!(await firstValueFrom(this._anonymousPortalSettings.spanishAcknowledgment$));
  }

  private evaluate(): void {
    const app = this._app;
    const status = this._status;
    if (!app || !status || this._dialogOpen || this._posting) {
      return;
    }
    const version = status.content?.version ?? null;
    const decision = decideGate({
      lang: this._transloco.getActiveLang(),
      signedIn: true,
      status: { required: status.required, version },
      pending: this.readPending(),
    });
    if (decision === 'save-pending' && version) {
      this.deletePending();
      this.post(app, version, 'pending');
    } else if (decision === 'prompt' && status.content) {
      void this.prompt(app, status.content);
    } else if (version && !status.required && this._transloco.getActiveLang() === 'es'
      && missingOnBehalf(this.appliedForCurrentApplication(), status.acknowledgedBorrowerIds).length) {
      this.post(app, version, 'on-behalf');
    }
  }

  private async prompt(app: LoadedApplication, content: SpanishAcknowledgmentContent): Promise<void> {
    this._dialogOpen = true;
    const accepted = await this.openDialog(content);
    this._dialogOpen = false;
    if (app !== this._app) {
      this.evaluate(); // the answer was for the application left behind; ask for the one loaded meanwhile
      return;
    }
    if (accepted) {
      this.deletePending();
      this.post(app, content.version, 'accepted');
      return;
    }
    this._transloco.setActiveLang('en');
    try { this._window.localStorage.setItem('preferred-lang', 'en'); } catch { /* ignore */ }
    this._uiLanguage.recordUserChoice('en');
  }

  private post(app: LoadedApplication, version: string, reason: PostReason): void {
    const onBehalfOf = missingOnBehalf(this.appliedForCurrentApplication(), this._status?.acknowledgedBorrowerIds ?? []);
    const key = `${app.applicationId}|${version}|${reason === 'on-behalf' ? onBehalfOf.join(',') : reason}`;
    if (reason !== 'accepted') {
      if (this._attempted.has(key)) {
        return;
      }
      this._attempted.add(key);
    }
    this._posting = true;
    const done = () => {
      this._posting = false;
      if (app === this._app) {
        this._reload.next(); // re-read: acknowledged now, or the wording changed (stale version → asked again)
      }
    };
    this._dataService.post(url(app.applicationId), { version, onBehalfOfBorrowerIds: onBehalfOf })
      .subscribe({ next: done, error: done });
  }

  private async openDialog(content: SpanishAcknowledgmentContent): Promise<boolean> {
    const ref = this._modal.open(SpanishAcknowledgmentDialogComponent, { ...Constants.modalOptions.large, keyboard: false });
    ref.componentInstance.content = content;
    ref.componentInstance.legalHref = legalDocumentsPageHref(this._navigation.companyGuid);
    try {
      return (await ref.result) === true;
    } catch {
      return false;
    }
  }

  private isSignedIn(): boolean {
    try {
      return !!signInIdentity(this._window.localStorage.getItem(Constants.authorization.authorizationDataKey));
    } catch {
      return false;
    }
  }

  private readPending(): PendingAcknowledgment | null {
    try {
      const pending = JSON.parse(this._window.sessionStorage.getItem(PENDING_ACK_KEY) ?? 'null');
      return usablePending(pending, this._navigation.companyGuid, Date.now());
    } catch {
      return null;
    }
  }

  private writePending(pending: PendingAcknowledgment): void {
    try { this._window.sessionStorage.setItem(PENDING_ACK_KEY, JSON.stringify(pending)); } catch { /* ignore */ }
  }

  private deletePending(): void {
    try { this._window.sessionStorage.removeItem(PENDING_ACK_KEY); } catch { /* ignore */ }
  }
}
